import React from 'react'
import { TextStyle } from 'react-native'
import { Text } from '@components'

type Translate = (key: string, params?: Record<string, string | number>) => string

export interface RichPart {
  text: string
  bold: boolean
}

// Placeholders put in the translated sentence where a bold value goes.
const marker = (index: number) => String.fromCharCode(0xe000 + index)

/**
 * A translated sentence cut where some of its values go, so they can be
 * shown in bold: « Garder contact avec **Léa Petit** ».
 */
export const richParts = (
  t: Translate,
  key: string,
  params: Record<string, string | number>,
  boldKeys: string[],
): RichPart[] => {
  const withMarkers: Record<string, string | number> = { ...params }
  boldKeys.forEach((name, index) => {
    withMarkers[name] = marker(index)
  })
  let rest = t(key, withMarkers)
  const parts: RichPart[] = []
  while (rest) {
    let at = -1
    let which = -1
    boldKeys.forEach((_, index) => {
      const found = rest.indexOf(marker(index))
      if (found >= 0 && (at < 0 || found < at)) {
        at = found
        which = index
      }
    })
    if (at < 0) {
      parts.push({ text: rest, bold: false })
      break
    }
    if (at > 0) parts.push({ text: rest.slice(0, at), bold: false })
    parts.push({ text: String(params[boldKeys[which]] ?? ''), bold: true })
    rest = rest.slice(at + 1)
  }
  return parts.filter((part) => part.text)
}

export const RichSentence = ({
  parts,
  color,
  style,
  numberOfLines,
}: {
  parts: RichPart[]
  color: string
  style?: TextStyle | TextStyle[]
  numberOfLines?: number
}) => (
  <Text type="body" color={color} style={style} numberOfLines={numberOfLines}>
    {parts.map((part, index) =>
      part.bold ? (
        <Text key={index} type="bodyBold" color={color}>
          {part.text}
        </Text>
      ) : (
        part.text
      ),
    )}
  </Text>
)

/** « de » or « d' » before a name, in French: « des nouvelles d'Hugo ». Empty in other languages. */
export const frenchDe = (name: string, language: string): string => {
  if (language !== 'fr') return ''
  return /^[aeiouhàâäéèêëîïôöùûü]/i.test(name.trim()) ? "d'" : 'de '
}
